import React from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { colors } from "utils";
import { useAuthReady } from "hooks/useAuthReady";
import { request } from "hooks/useOkoApi/useOkoApi";
import { toTaggedQuotes } from "hooks/useTaggedQuotes";
import { logError, LogCategory } from "utils/sentryLogger";
import { criticalTagsOnly, SELF_HARM_TAG } from "utils/taggedQuotes";
import {
  IncidentCountsSummary,
  IncidentsDisclaimer,
  IncidentTagChip,
} from "../shared/criticalIncidentsUi";

const POLL_WHILE_EMPTY_MS = 5000;

const flaggedQuotes = (data) => criticalTagsOnly(toTaggedQuotes(data)).filter((q) => q.quote);

/**
 * The flagged quotes of a running session. Its own cache entry, not the
 * report's: that one never goes stale, and a live session keeps gaining
 * quotes. Keyed on the latest concern so each one refetches (showing the
 * previous list meanwhile), and polled while the flag is up but no quote has
 * landed yet, since the concern can be announced before its quote is readable.
 */
export const useLiveTaggedQuotes = (sessionId, enabled, latestConcernId) => {
  const authReady = useAuthReady();
  const { data, isInitialLoading, isError, refetch } = useQuery({
    queryKey: ["live-tagged-quotes", sessionId, latestConcernId ?? null],
    queryFn: () =>
      request(`account/session_history_tagged_quotes/${sessionId}`, { method: "GET" }),
    enabled: Boolean(sessionId) && enabled && authReady,
    keepPreviousData: true,
    refetchInterval: (raw) => (raw && flaggedQuotes(raw).length === 0 ? POLL_WHILE_EMPTY_MS : false),
    onError: (error) =>
      logError("LiveSessions: flagged quotes failed to load", error, {
        category: LogCategory.ERROR,
        sessionId,
      }),
  });
  return { quotes: flaggedQuotes(data), isInitialLoading, isError, refetch };
};

// Session start plus the tagger's "MM:SS" offset, as a wall-clock date and
// time the teacher can place.
export const quoteTime = (sessionStartedAt, quoteTimestamp) => {
  if (!sessionStartedAt) return null;
  const start = new Date(sessionStartedAt);
  const match = /^(\d+):(\d{2})$/.exec(String(quoteTimestamp || ""));
  if (Number.isNaN(start.getTime()) || !match) return null;
  const at = new Date(start.getTime() + (Number(match[1]) * 60 + Number(match[2])) * 1000);
  const date = at.toLocaleDateString(undefined, {
    weekday: "short",
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${date} ${time}`;
};

// One count per tag across quotes, self harm first: a quote carrying both tags
// counts toward each.
const tagCounts = (entries) => {
  const counts = [];
  for (const { tags } of entries) {
    for (const tag of tags) {
      const row = counts.find((c) => c.tag === tag);
      if (row) row.count += 1;
      else counts.push({ tag, count: 1 });
    }
  }
  return counts.sort((a, b) => (b.tag === SELF_HARM_TAG) - (a.tag === SELF_HARM_TAG));
};

// The live pill's Safety Concerns tab. Unlike the report's, it lists each quote
// once, in the order it was said, with every tag it carries and when it was said.
export const LiveSafetyConcerns = ({ taggedQuotes, sessionStartedAt, onQuoteClick }) => {
  const entries = criticalTagsOnly(taggedQuotes).filter((q) => q.quote);

  return (
    <Wrapper>
      <IncidentCountsSummary counts={tagCounts(entries)} />
      <Disclaimer>
        <IncidentsDisclaimer />
      </Disclaimer>
      <List>
        {entries.map((entry, i) => {
          const when = quoteTime(sessionStartedAt, entry.quote_timestamp);
          return (
            <Item key={entry.id ?? i}>
              <Line>
                <span>
                  <Name>{entry.studentName}</Name>
                  {when && ` ${when}`}
                </span>
                {entry.tags.map((tag) => (
                  <IncidentTagChip key={tag} tag={tag} />
                ))}
              </Line>
              <Quote type="button" onClick={() => onQuoteClick({ text: entry.quote })}>
                &ldquo;{entry.quote}&rdquo;
              </Quote>
            </Item>
          );
        })}
      </List>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Disclaimer = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
`;

const List = styled.ul`
  margin: 0;
  padding-left: 24px;
  list-style: disc;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Item = styled.li`
  font-family: Inter, sans-serif;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: #002e19;
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const Name = styled.span`
  font-weight: 700;
`;

const Quote = styled.button`
  display: block;
  margin-top: 8px;
  padding: 0;
  background: none;
  border: none;
  text-align: left;
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #524fc9;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${colors.blue800};
  }
`;
